import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import intl from 'react-intl-universal';
import { captureException } from '@sentry/react';

import { UserType } from 'user/User';
import { Page404 } from './components/pages/Page404/Page404';
import { storedUserRole } from 'utils/storedUserRole';
import { lazyPage } from 'utils/lazyPage';

// This file was Router.tsx, imported as 'Router'. webpack looks in node_modules before src, and
// Express 5 (webpack-dev-server 6) installs a package named `router`: on a case-insensitive disk
// the bare 'Router' resolved to it. A name no package has keeps the two apart.

// Each page is its own chunk, loaded the first time it is shown (#226).
const ActivityPage = lazyPage(async () => (await import('./activity/ActivityPage')).ActivityPage);
const SearchPage = lazyPage(async () => (await import('./search/SearchPage')).SearchPage);
const AssignmentsPage = lazyPage(async () => (await import('assignment/view/AssignmentsPage')).AssignmentsPage);
const TeachingPathsPage = lazyPage(async () => (await import('teachingPath/view/TeachingPathsPage')).TeachingPathsPage);
const StudentsListPage = lazyPage(async () => (await import('student/view/StudentsListPage')).StudentsListPage);
const EvaluationPage = lazyPage(async () => (await import('evaluation/EvaluationPage/EvaluationPage')).EvaluationPage);
const AssignmentDistributeList = lazyPage(async () => (await import('evaluation/EvaluationPage/AssignmentDistributeList')).AssignmentDistributeList);
const TeachingPathDistributeList = lazyPage(async () => (await import('evaluation/EvaluationPage/TeachingPathDistributeList')).TeachingPathDistributeList);

const AssignmentsPageComponent = () => <AssignmentsPage isStudent />;
const TeachingPathsPageComponent = () => <TeachingPathsPage isStudent />;
const SearchPageCompontent = () => <SearchPage />;

const ContentManagerAssignments = () => <AssignmentsPage isContentManager />;
const ContentManagerTeachingPaths = () => <TeachingPathsPage isContentManager />;

// A hook rather than injected props: v7's <Route element> hands the element nothing, and
// this is a function component, so it can simply ask.
const EvaluationRedirect = () => {
  const location = useLocation();

  return <Navigate to={`/evaluation/assignments${location.search}`} replace />;
};
const ActivityRedirect = () => <Navigate to={'/activity'} replace />;

const EvaluationPageRoutes = () => (
  <EvaluationPage>
    <Routes>
      {/*
        * Relative, like every other descendant <Routes> here: the parent is `/evaluation/*`,
        * and an absolute path inside a descendant never matches once the parent has consumed
        * its prefix. It does not error — it falls through to the `*` below, which is why this
        * rendered a 404 rather than anything obviously broken.
        */}
      <Route
        path="/"
        element={<EvaluationRedirect />}
      />
      <Route
        path="assignments"
        element={<AssignmentDistributeList />}
      />
      <Route
        path="teaching-paths"
        element={<TeachingPathDistributeList />}
      />
      <Route
        path="*"
        element={<Page404 />}
      />
    </Routes>
  </EvaluationPage>
);


/**
 * Every path below that ends in `/*` does so because its element renders a `<Routes>` of its
 * own. v5's `<Route path="/search">` matched `/search` and everything under it, which is how
 * those pages took over their own sub-paths. v7 matches exactly unless the path ends in a
 * splat — so without it `/search/article` misses this route entirely and lands on the 404,
 * and `/search` matches but leaves the page's `<Routes>` nothing to match against, which
 * renders blank.
 *
 * `/activity` has no splat on purpose: ActivityPage is a leaf with no routes beneath it.
 */
const KNOWN_ROLES: Array<string> = [UserType.Teacher, UserType.Student, UserType.ContentManager];

const isKnownRole = (role: unknown): boolean => (
  typeof role === 'string' && KNOWN_ROLES.includes(role)
);

/**
 * What the main pane shows when the stored role is not one this router knows.
 *
 * Each role group below is a `<Routes>` with its own 404, but the groups themselves are gated
 * on an exact string match — so any other value rendered **nothing at all**: an empty main
 * pane, no route, no message, nothing in the console. `SUPER-CM` is a real value of this
 * enum and has no group here, and an absent `USER_DATA` reads as `undefined`.
 *
 * A blank pane is the hardest failure in this application to diagnose from a user's
 * description, and it has cost several rounds of browser probing more than once. This says
 * something instead, and reports itself.
 *
 * It deliberately does not redirect to `/logout`: a transiently missing `USER_DATA` would
 * then sign the user out on the way in, which is a worse failure than a visible message.
 */
const UnknownRole = ({ role }: { role: unknown }) => {
  React.useEffect(
    () => {
      const message = `No routes are defined for the role ${JSON.stringify(role)}, so the main pane is empty.`;
      captureException(new Error(message));
      console.error(message);
    },
    [role]
  );

  return (
    <div className="ErrorBoundary" role="alert">
      <p className="ErrorBoundary__message">{intl.get('generals.error_boundary.message')}</p>
    </div>
  );
};

export const Router = () => {
  const currentUserRole = storedUserRole();

  const teacherRoutes = (
    <Routes>
      <Route
        path="/"
        element={<ActivityRedirect />}
      />
      <Route path="/activity" element={<ActivityPage role={UserType.Teacher} />} />

      <Route
        path="/search/*"
        element={<SearchPageCompontent />}
      />

      <Route
        path="/assignments/*"
        element={<AssignmentsPage />}
      />
      <Route
        path="/teaching-paths/*"
        element={<TeachingPathsPage />}
      />
      <Route
        path="/students/*"
        element={<StudentsListPage />}
      />
      <Route
        path="/evaluation/*"
        element={EvaluationPageRoutes()}
      />
      <Route
        path="/*"
        element={<Page404 />}
      />
    </Routes>
  );

  const studentRoutes = (
    <Routes>
      <Route
        path="/"
        element={<ActivityRedirect />}
      />
      <Route path="/activity" element={<ActivityPage role={UserType.Student} />} />
      <Route
        path="/search/*"
        element={<SearchPageCompontent />}
      />
      <Route
        path="/teaching-paths/*"
        element={<TeachingPathsPageComponent />}
      />
      <Route
        path="/assignments/*"
        element={<AssignmentsPageComponent />}
      />
      <Route
        path="/*"
        element={<Page404 />}
      />
    </Routes>
  );

  const contentManagerRoutes = (
    <Routes>
      <Route
        path="/"
        element={<ActivityRedirect />}
      />
      <Route path="/activity" element={<ActivityPage role={UserType.ContentManager} />} />
      <Route
        path="/search/*"
        element={<SearchPageCompontent />}
      />
      <Route
        path="/assignments/*"
        element={<ContentManagerAssignments />}
      />
      <Route
        path="/teaching-paths/*"
        element={<ContentManagerTeachingPaths />}
      />
      <Route
        path="/*"
        element={<Page404 />}
      />
    </Routes>
  );

  return (
  /*
   * A fragment, not a <Routes>. Each role group below is itself a <Routes>, and v6 rejects a
   * <Routes> nested directly inside another — it accepts only <Route> and fragment children.
   * The outer wrapper was never doing anything: exactly one role group renders.
   */
    <>
      {currentUserRole === UserType.Teacher && teacherRoutes}
      {currentUserRole === UserType.Student && studentRoutes}
      {currentUserRole === UserType.ContentManager && contentManagerRoutes}
      {!isKnownRole(currentUserRole) && <UnknownRole role={currentUserRole} />}
    </>
  );
};
